import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import _ from "lodash";
import { Breadcrumbs, Header, Workspace, LoadingState } from "components/Shared";
import { Spinner, CheckBox } from "v2/components/shared";
import { getCondition, generateConditionPdf, previewConditionPdf, updateConditionFillData,
         getConditionAutoPopulateDictionary, AutoPopulateDictionary } from "actions/conditions";
import { getForm } from "actions/forms";
import AvailableFieldsModal from "./AvailableFieldsModal";

const PANEL_HEIGHT = 720;

const humanise = (k: string) =>
	k.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// A ticked checkbox round-trips through filled_data as JSON, so it can come
// back as a real boolean or as "true" / "1" depending on how it was written.
// Mirrors Forms::DocxRenderer#truthy? on the API side — keep the two in step.
const isChecked = (value: any) =>
	value === true ||
	["1", "true", "on", "yes", "checked"].includes(String(value ?? "").trim().toLowerCase());

const FormFill = ({ match, history }: RouteComponentProps<{ id: string }>) => {
	const dispatch = useDispatch();
	const conditionId = match.params.id;
	const condition = useSelector((state: any) => _.find(state.conditions, { id: conditionId }) as any);
	const form = useSelector((state: any) =>
		condition && condition.form_id ? _.find(state.forms, { id: condition.form_id }) : null
	);
	// Values are mixed: {{token}} fields hold strings, checkbox fields hold
	// booleans. Both live in the same filled_data hash server-side.
	const [filledData, setFilledData] = useState<Record<string, any>>({});
	const [generating, setGenerating] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [previewVersion, setPreviewVersion] = useState(0);
	const [bootstrappedPreview, setBootstrappedPreview] = useState(false);
	const [dictionary, setDictionary] = useState<AutoPopulateDictionary>({});
	const [openForKey, setOpenForKey] = useState<string | null>(null);
	// Transient preview URL from /preview_pdf. Falls back to the persisted
	// generated_document_url (set by "Generate PDF & Set Up Signing") if the
	// user has already finalised this condition in a prior visit.
	const [previewUri, setPreviewUri] = useState<string | undefined>(undefined);

	useEffect(() => {
		dispatch(getCondition(conditionId) as any);
	}, [dispatch, conditionId]);

	useEffect(() => {
		if (condition?.form_id && !form) {
			dispatch(getForm(condition.form_id) as any);
		}
		// Server-driven auto-populate; the client never merges defaults itself.
		if (condition?.filled_data && Object.keys(condition.filled_data).length) {
			setFilledData((prev) => (Object.keys(prev).length ? prev : condition.filled_data));
		}
	}, [condition, form, dispatch]);

	useEffect(() => {
		if (!conditionId) return;
		// Powers the "Insert a field" modal. The client does NOT prefill from this —
		// auto-populate is server-driven (see condition.auto_populated_at); the form
		// trusts condition.filled_data.
		(dispatch(getConditionAutoPopulateDictionary(conditionId) as any) as Promise<AutoPopulateDictionary>)
			.then((dict) => setDictionary(dict || {}));
	}, [dispatch, conditionId]);

	const handleOpenFieldsModal = (key: string) => setOpenForKey(key);
	const handleCloseFieldsModal = () => setOpenForKey(null);

	// Auto-render a preview PDF when the page opens.
	//   - If the user has previously finalised (generated_document_url is set),
	//     show that file straight away — don't burn a render call.
	//   - Otherwise render a transient preview via /preview_pdf. This does
	//     NOT create a Document row; only "Generate PDF & Set Up Signing"
	//     does that (see onContinue below).
	useEffect(() => {
		if (!condition || bootstrappedPreview) return;
		if (!condition.form_id) return;
		// Only trust a persisted generated URL when a generated document actually
		// exists. After an Undo the generated PDF is deleted and
		// generated_document_id is cleared, so we must regenerate a fresh preview
		// rather than point the iframe at the now-missing S3 object (NoSuchKey).
		const persistedUrl = (condition.generated_document_id &&
			(condition.generated_document_url ||
				_.get(condition, "generated_document.revisions[0].signed_uri"))) as string | undefined;
		if (persistedUrl) {
			setPreviewUri(persistedUrl);
			setPreviewVersion((n) => n + 1);
			setBootstrappedPreview(true);
			return;
		}
		setBootstrappedPreview(true);
		setGenerating(true);
		dispatch(previewConditionPdf(conditionId) as any)
			.then((url: string | undefined) => {
				if (url) setPreviewUri(url);
				setPreviewVersion((n) => n + 1);
			})
			.catch((e: any) => setError(e?.message || "Failed to generate preview"))
			.finally(() => setGenerating(false));
	}, [condition, bootstrappedPreview, conditionId, dispatch]);

	if (!condition) return <LoadingState />;

	// The CONDITION's field lists win over the form's. GET /conditions/:id is the
	// request that runs Forms::RefreshSnapshotFromTemplate, so after a template
	// replace it is the only response guaranteed to carry the new document's
	// fields. `form` comes out of state.forms, a cumulative cache that is never
	// invalidated, so a snapshot fetched on an earlier visit keeps the fields of
	// the document it was fetched with — and getForm above does not re-fetch
	// once it is cached. `??`, not `||`: an empty list from the server is a real
	// answer (a template with no {{tokens}}), only a missing one falls back.
	const placeholders: string[] = (condition?.form_placeholders ?? form?.placeholders ?? []) as string[];
	// Checkbox bookmarks/tags the template declares, kept apart from the text
	// placeholders so each renders with the right control. See
	// Forms::ExtractCheckboxes on the API side.
	const checkboxFields: string[] = (condition?.form_checkbox_fields ??
		form?.checkbox_fields ??
		[]) as string[];

	// "Refresh preview" — saves the current field values, then re-renders the
	// transient preview PDF. Crucially, this calls /preview_pdf and NOT
	// /generate_pdf, so it never creates a Document or sets
	// generated_document_id. Only "Generate PDF & Set Up Signing" (onContinue)
	// commits a persisted Document.
	const regeneratePreview = async () => {
		setGenerating(true);
		setError(null);
		try {
			await dispatch(updateConditionFillData(conditionId, filledData, condition.form_id) as any);
			const url = await dispatch(previewConditionPdf(conditionId) as any);
			if (url) setPreviewUri(url);
			setPreviewVersion((n) => n + 1);
		} catch (e) {
			setError((e as any)?.message || "Failed to regenerate preview");
		} finally {
			setGenerating(false);
		}
	};

	const onContinue = async () => {
		setSubmitting(true);
		setError(null);
		try {
			await dispatch(updateConditionFillData(conditionId, filledData, condition.form_id) as any);
			await dispatch(generateConditionPdf(conditionId, filledData) as any);
			// Always hand off to Configure Signers — the amendment path (re-generating
			// after an Undo) included. Setting up signing is part of this step, and
			// after an Undo the user may well want to change the signing order or who
			// signs; skipping the page left them no way to do that. The page pre-fills
			// from condition.signers / condition.signing_order, so keeping the existing
			// setup is a single click. Re-submitting is safe: the backend diffs the
			// selection (Condition#assign_signers! + sync_signing_children!) and
			// re-notifying an already-notified signer is a no-op (Signer#notify!).
			// The "form amended" notice still fires from Forms::GeneratePdf above.
			history.push(`/conditions/${conditionId}/signers`);
		} catch (e) {
			setError((e as any)?.message || "Failed to generate PDF");
		} finally {
			setSubmitting(false);
		}
	};

	const code = form?.code || condition?.form_code;
	const name = form?.name || condition?.form_name;
	const formTitle = name || code || condition.name;

	return (
		<>
			<Breadcrumbs
				items={[
					// Deep-link to the Conditions tab: Workspace reads the active tab
					// from the URL hash, so a bare /tasks/:id would land the user on
					// the default Comments tab instead of the list this form came from.
					{
						title: "tasks",
						link: condition.task_id ? `/tasks/${condition.task_id}#conditions` : "/tasks"
					},
					{ title: condition.name || "condition" },
					{ title: "create_form" }
				]}
			/>
			<Header title={formTitle} subtitle={`Condition: ${condition.name}`} />
			<Workspace id="condition-fill">
				{/* Soften the placeholder text inside the field column so the
				    {{token}} hint reads as faint guidance rather than competing
				    with real values. ::placeholder can't be set via inline style,
				    so we scope a tiny rule to this workspace via the id selector. */}
				<style>{`
					#condition-fill .form-control::placeholder {
						color: #9CA3AF;
						opacity: 0.45;
					}
					/* Match the theme's btn-outline hover (grey at rest, theme
					   blue fill on hover — same as .btn-outline-primary) for the
					   "?" insert-field buttons, which Bootstrap would otherwise
					   hover grey. */
					#condition-fill .input-group-append .btn-outline-secondary:hover:not(:disabled),
					#condition-fill .input-group-append .btn-outline-secondary:focus:not(:disabled) {
						background-color: #00c6d7;
						border-color: #00c6d7;
						color: #fff;
						box-shadow: 0 1px 6px 4px rgba(0, 0, 0, 0.05);
					}
				`}</style>
				{/* Primary CTA pulled to the top of the workspace and bound to the
				    same col-md-4 lane as the Fields card so its right edge lines
				    up with the card's right edge below. */}
				<div className="row">
					<div className="col-md-8" />
					<div className="col-md-4 d-flex justify-content-end">
						<button
							className="btn btn-primary d-flex align-items-center justify-content-center"
							onClick={onContinue}
							disabled={submitting || !form}
							style={{
								// Keep the button width steady while the label is swapped
								// for the spinner, so it doesn't collapse to spinner-size
								// and jump around the layout.
								minWidth: 290,
								// Bootstrap's :disabled drops opacity to 0.65, making the
								// button look grey. Force opacity back to 1 while
								// submitting so the spinner sits on a primary-blue surface
								// (matches the v2 sign-in button's loading look).
								opacity: submitting ? 1 : undefined,
								cursor: submitting ? "wait" : undefined,
							}}
						>
							{submitting ? (
								<Spinner tcp={false} width="20" height="20" />
							) : (
								"Generate PDF & Set Up Signing →"
							)}
						</button>
					</div>
				</div>

				{/* Explicit spacer between the CTA and the panels below. Using a
				    real DOM element with a fixed height sidesteps Bootstrap's
				    .row negative-margin gutters that were collapsing margin-
				    based spacing here. */}
				<div aria-hidden="true" style={{ height: 12 }} />

				<div className="row">
					<div className="col-md-8">
						<div className="card d-flex flex-column" style={{ height: PANEL_HEIGHT }}>
							<div className="card-header d-flex justify-content-between align-items-center">
								<h5 className="mb-0">Preview</h5>
								<button
									className="btn btn-sm btn-primary"
									onClick={regeneratePreview}
									// Also block during the final hand-off so the user can't
									// kick off a refresh while the page is mid-redirect.
									disabled={generating || submitting || !form}
								>
									{generating ? "Generating…" : "Refresh preview"}
								</button>
							</div>
							<div className="card-body p-0" style={{ flex: 1, minHeight: 0, position: "relative" }}>
								{!generating && !previewUri && (
									<div className="p-4 text-muted">
										{form ? "Preparing preview…" : "This condition has no form linked."}
									</div>
								)}
								{previewUri && (
									<iframe
										key={previewVersion}
										// PDF viewer fragment params: hide the page-thumbnails sidebar
										// (navpanes=0) and any thumbnail panel (pagemode=none) by
										// default. The toolbar stays so users can still zoom / print.
										src={`${previewUri}${previewUri.includes("#") ? "&" : "#"}toolbar=1&navpanes=0&pagemode=none`}
										title="Form preview"
										style={{ width: "100%", height: "100%", border: 0 }}
									/>
								)}
								{generating && (
									<div
										style={{
											position: "absolute",
											inset: 0,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											background: "rgba(255, 255, 255, 0.7)",
											zIndex: 10,
										}}
									>
										<LoadingState />
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="col-md-4">
						<div className="card d-flex flex-column" style={{ height: PANEL_HEIGHT }}>
							<div className="card-header p-0">
								<ul className="nav nav-tabs" role="tablist">
									<li className="nav-item">
										<button
											type="button"
											className="nav-link active"
										>
											Fields
										</button>
									</li>
								</ul>
							</div>

							{/* Fields panel — always visible (only tab remaining) */}
							{/* Explicit padding so labels and inputs aren't flush against
							    the card edges. The global .form-group CSS uses negative
							    margins in places, so we set padding here instead of relying
							    on Bootstrap's card-body default; the per-row marginLeft/Right
							    overrides neutralise any ".form-group" negative margin so the
							    rows respect this padding. */}
							<div
								className="card-body"
								style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 20px" }}
							>
								{!form && (
									<div className="alert alert-warning">This condition has no form linked.</div>
								)}
								{form && placeholders.length === 0 && checkboxFields.length === 0 && (
									<div className="alert alert-info">No placeholders declared for this form.</div>
								)}
								{/* Match the project-creation form styling: form-group + label/span
								    + input-group + form-control. The global stylesheet themes these
								    classes (label colour, focus state, prefix/suffix slots), so we
								    inherit the same look as Tenant / Lease / Tenancy steps. */}
								{placeholders.map((key) => (
									<div
										key={key}
										id={`form-group-${key.replace("_", "-")}`}
										className={`form-group form-group-text form-group-${key.replace("_", "-")} ${filledData[key] ? "is-fulfilled" : ""}`}
										style={{ marginLeft: 0, marginRight: 0 }}
									>
										<label htmlFor={`form-control-input-${key}`}>
											{/* Show the raw placeholder key (e.g. requested_by) so it
											    matches the {{...}} token in the document exactly */}
											<span>{key}</span>
											<div
												className="input-group"
												id={`form-control-input-group-${key.replace("_", "-")}`}
											>
												<input
													id={`form-control-input-${key}`}
													name={key}
													type="text"
													className="form-control"
													autoComplete="off"
													// Show the underlying token (e.g. {{date}}) as
													// placeholder text inside the field — disappears
													// as soon as the user types, so the hint and the
													// value share the same row.
													placeholder={"{{" + key + "}}"}
													value={filledData[key] || ""}
													// Lock the inputs once the user has committed to
													// generating the final PDF, so values can't change
													// mid-render and end up out of sync with the doc
													// the signers will receive.
													disabled={submitting}
													onChange={(e) => {
														const value = e.target.value;
														setFilledData((d) => ({ ...d, [key]: value }));
													}}
												/>
												<div className="input-group-append">
													<button
														type="button"
														className="btn btn-outline-secondary"
														aria-label={`Insert placeholder for ${humanise(key)}`}
														title="Insert a field"
														disabled={submitting}
														onClick={() => handleOpenFieldsModal(key)}
														// Round icon button — overrides the squared-off
														// corners Bootstrap applies inside input-groups.
														style={{
															borderRadius: "50%",
															width: 38,
															height: 38,
															padding: 0,
															marginLeft: 8,
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
														}}
													>
														?
													</button>
												</div>
											</div>
										</label>
									</div>
								))}
								{checkboxFields.map((key) => (
									<div
										key={key}
										id={`form-group-${key.replace("_", "-")}`}
										className={`form-group form-group-checkbox form-group-${key.replace("_", "-")} ${
											isChecked(filledData[key]) ? "is-fulfilled" : ""
										}`}
										style={{ marginLeft: 0, marginRight: 0 }}
									>
										<label
											htmlFor={`form-control-checkbox-${key}`}
											style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
										>
											<CheckBox
												id={`form-control-checkbox-${key}`}
												name={key}
												checked={isChecked(filledData[key])}
												// Locked once the user commits to generating the final PDF,
												// matching the text inputs above.
												disabled={submitting}
												onChange={(e: any) => {
													const checked = !!e?.target?.checked;
													setFilledData((d) => ({ ...d, [key]: checked }));
												}}
											/>
											{/* Raw bookmark/tag, so it matches what the template author
												typed into Word's Bookmark or Tag box exactly */}
											<span style={{ marginLeft: 10 }}>{key}</span>
										</label>
									</div>
								))}
								{error && <div className="alert alert-danger py-2">{error}</div>}
							</div>
						</div>
					</div>
				</div>

				<AvailableFieldsModal
					isOpen={openForKey !== null}
					dictionary={dictionary}
					onClose={handleCloseFieldsModal}
					onInsert={(token) => {
						if (!openForKey) return;
						// Replace the field's existing value with the selected token
						setFilledData((d) => ({ ...d, [openForKey]: token }));
						setOpenForKey(null);
					}}
				/>
			</Workspace>
		</>
	);
};

export default withRouter(FormFill);
